// The weekly recap (RECAP.md): a reading column with the headline, the
// standfirst, my week in one sentence, and the sections. Lines that tell a
// game open the matchup; the awards sit at the end. Share draws the card.
// Drawn through the kit (LOOKS.md Part 7).
//
// OR READ AS A STORY (B-405, PLAN-2026-10-17 §5): the same words as cards
// that swipe — the headline, my week, each section, the awards — one at a
// time, as a phone tells a story. A phone opens on the story, a wider screen
// on the page, and either switches; the choice is kept for the visit.

import { Fragment, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { RecapView } from '@fsp/domain';
import { useLeagueBase, useMyTeam, useRecap } from '../data/hooks';
import { LiveDot, TeamMark } from '../kit';
import { TeamFigure } from '../kit/TeamFigure';
import { Button, Caption, Cover, ListRow, RowName, Segments, Text, Title } from '../kit/ui';
import { CardRow } from '../kit/cards';
import { useRemembered } from '../shell/tabMemory';
import '../styles/screens/waivers.css';
import '../styles/screens/league-screens.css';
import '../styles/screens/home.css';
import { useTier } from '../shell/useTier';
import { DetailSheet } from '../templates/DetailSheet';
import { WeekStepper } from './shared/WeekStepper';
import { AwardIcon, SharePanel } from './shared/AwardKit';
import { FromLeague } from './shared/FromLeague';
import { LinkedText, TeamName } from './shared/Names';

export function RecapScreen() {
  const [params] = useSearchParams();
  const periodParam = params.get('period');
  const period = periodParam && /^\d+$/.test(periodParam) ? Number(periodParam) : undefined;
  // A real league's recap is a read (PLAN-2026-09-26-season-end S4).
  return (
    <FromLeague snapshot={useRecap(period)}>{(recap) => <RecapBody recap={recap} />}</FromLeague>
  );
}

function RecapBody({ recap }: { recap: RecapView }) {
  const [, setParams] = useSearchParams();
  const navigate = useNavigate();
  const base = useLeagueBase();
  const tier = useTier();
  const phone = tier === 'phone';
  const myTeam = useMyTeam();
  const [share, setShare] = useState(false);
  const [chosen, setChosen] = useRemembered<'story' | 'page' | null>('recap:view', null);
  const view = chosen ?? (phone ? 'story' : 'page');

  const go = (p: number) => setParams({ period: String(p) }, { replace: true });
  const has = recap.headline !== null;
  // THE WEEK'S WINNER CHEERS (B-529): the high score's team, its character
  // pleased beside the headline. A league with its awards off has none to name.
  const star = recap.awards.find((c) => c.awardId === 'high-score')?.winners[0]?.team ?? null;
  const cheer = star ? (
    <div className="fsp-recap-star">
      <TeamFigure team={star} mood="pleased" height={phone ? 84 : 112} />
      <span className="fsp-sub">
        <TeamName team={star} />
      </span>
    </div>
  ) : null;

  const header = (
    <div className="fsp-waivers-head">
      <div className="fsp-waivers-head-body">
        <Title>Recap</Title>
        <div className="fsp-sub fsp-ui-wrap-text">
          Written from the week's final numbers. Quote it in chat.
        </div>
      </div>
      {/* A WEEK PICKED AS WELL AS STEPPED (B-418). */}
      <WeekStepper
        periods={recap.periods}
        current={recap.period.index}
        label={recap.period.label}
        onGo={go}
      />
      {has ? (
        <Segments
          label="Read it as"
          items={[
            { id: 'story', label: 'Story' },
            { id: 'page', label: 'Page' },
          ]}
          value={view}
          onChange={(v) => setChosen(v as 'story' | 'page')}
        />
      ) : null}
      {has ? (
        <Button size="xs" icon="share" onClick={() => setShare(true)}>
          Share
        </Button>
      ) : null}
    </div>
  );

  /** A section's lines: a game's opens its matchup, the names their cards and pages. */
  const lines = (s: RecapView['sections'][number]) =>
    s.lines.map((l, i) => (
      <div
        key={i}
        className={`fsp-recap-line ${l.emphasis ? 'emphasis' : ''} ${l.matchupId ? 'fsp-row-clickable fsp-cover-host' : ''}`}
      >
        {/* A game's line still opens its game; the names in it open the
            player's card or the manager's team, above that door rather than
            inside it (B-220). */}
        {l.matchupId ? (
          <Cover label="Open the matchup" to={`${base}/matchup/${l.matchupId}`} />
        ) : null}
        {l.team ? <TeamMark team={l.team} size={18} /> : null}
        <span>
          <LinkedText text={l.text} names={l.names} />
        </span>
      </div>
    ));
  const awardList = (
    <div className="fsp-surface">
      {recap.awards.map((c) => (
        <ListRow
          key={c.id}
          leadSize="md"
          lead={
            <span className="fsp-award-icon fsp-chat-card-glyph" data-tone={c.tone}>
              <AwardIcon icon={c.icon} size={13} />
            </span>
          }
          trail={
            <Text weight="bold" numeric>
              {c.winners[0]?.valueLabel}
            </Text>
          }
          onClick={() => navigate(`${base}/awards?period=${c.period}`)}
          cover={`${c.name}: the week's awards`}
        >
          <RowName>
            {c.name}
            <span className="fsp-muted">
              {' · '}
              {c.winners.map((w, j) => (
                <Fragment key={w.team.id}>
                  {j > 0 ? ' and ' : ''}
                  <TeamName team={w.team} />
                </Fragment>
              ))}
            </span>
          </RowName>
        </ListRow>
      ))}
      <div className="fsp-home-links">
        <Button to={`${base}/awards?period=${recap.period.index}`} variant="quiet" size="xs">
          All awards
        </Button>
      </div>
    </div>
  );
  // THE STORY: the same words, a card each.
  const card = (key: string, body: ReactNode) => ({
    key,
    content: <div className="fsp-surface fsp-story-card">{body}</div>,
  });
  const story = [
    card(
      'cover',
      <>
        <Caption>{recap.period.label} recap</Caption>
        {cheer}
        <h2 className="fsp-disp fsp-story-headline">{recap.headline}</h2>
        {recap.standfirst ? <p className="fsp-recap-standfirst">{recap.standfirst}</p> : null}
      </>,
    ),
    ...(recap.mine
      ? [
          card(
            'mine',
            <>
              <Caption>Your week</Caption>
              <p className="fsp-story-mine">{recap.mine}</p>
            </>,
          ),
        ]
      : []),
    ...recap.sections.map((s) =>
      card(
        s.id,
        <>
          <Caption>{s.title}</Caption>
          {lines(s)}
        </>,
      ),
    ),
    ...(recap.awards.length
      ? [
          card(
            'awards',
            <>
              <Caption>The awards</Caption>
              {awardList}
            </>,
          ),
        ]
      : []),
  ];

  return (
    <div className="fsp-screen fsp-flow">
      <div className="fsp-pinned">{header}</div>
      <div className="fsp-regions fsp-board-one">
        <section className="fsp-region fsp-region-plain" aria-label="Recap">
          {!has ? (
            <div className="fsp-empty">
              {recap.status === 'live' ? (
                <span className="fsp-awards-state">
                  <LiveDot />
                  The recap lands when the week's scores are final.
                </span>
              ) : (
                'Nothing to recap yet. Play a week.'
              )}
            </div>
          ) : view === 'story' ? (
            <div className="fsp-recap fsp-recap-story" data-phone={phone || undefined}>
              <CardRow label={`${recap.period.label} recap`} what="page" items={story} />
            </div>
          ) : (
            <article className="fsp-recap" data-phone={phone || undefined}>
              {cheer}
              <h1 className="fsp-disp fsp-recap-headline">{recap.headline}</h1>
              <p className="fsp-recap-standfirst">{recap.standfirst}</p>
              {recap.mine ? (
                <div className="fsp-surface fsp-recap-mine">
                  <Caption>Your week</Caption>
                  <div>{recap.mine}</div>
                </div>
              ) : null}
              {recap.sections.map((s) => (
                <section key={s.id} className="fsp-recap-section" aria-label={s.title}>
                  <Caption>{s.title}</Caption>
                  {lines(s)}
                </section>
              ))}
              {recap.awards.length ? (
                <section className="fsp-recap-section" aria-label="The awards">
                  <Caption>The awards</Caption>
                  {awardList}
                </section>
              ) : null}
            </article>
          )}
        </section>
      </div>
      {has && myTeam ? (
        <DetailSheet opened={share} onClose={() => setShare(false)} title="Share the recap">
          <SharePanel
            input={{
              kicker: `${recap.period.label} recap`,
              headline: recap.period.label,
              subline: recap.league.name,
              line: `${recap.headline} ${recap.standfirst ?? ''}`.trim(),
              footer: `${recap.league.name} · ${recap.league.season}`,
              team: myTeam,
              tone: 'fun',
            }}
            text={`${recap.period.label} in ${recap.league.name}: ${recap.headline} ${recap.standfirst ?? ''}`.trim()}
          />
        </DetailSheet>
      ) : null}
    </div>
  );
}
